import { RouteWithGates } from '../api/routes';
import { MergeRoutesResult } from '../types/Gate';
import { connectedGroups, referenceLatitude, routeSection } from './routeGeometry';
import { plural } from './plural';

/**
 * How far apart two routes' measured sections may be and still be merged.
 * Mirrors the server: AverageRoute and the gate re-projection both look for
 * intersections within CombineRoutes' max_radius of 100 m, so routes further
 * apart than this produce a merged route with no gates on it.
 */
export const ROUTE_MERGE_MAX_DISTANCE_M = 100;

export interface RouteMergeExclusion {
  route: RouteWithGates;
  /** Metres to the nearest candidate's section, or null when the route has no geometry. */
  distanceM: number | null;
}

export interface RouteMergeCandidates {
  candidates: RouteWithGates[];
  excluded: RouteMergeExclusion[];
}

/**
 * Split a selection of routes into the ones that can be merged together and
 * the ones that cannot. Two routes are merge candidates when their measured
 * sections come within ROUTE_MERGE_MAX_DISTANCE_M of each other; the
 * candidates are the largest such connected group. Everything else is
 * excluded, with its distance to the nearest candidate so the UI can say why.
 */
export function findRouteMergeCandidates(routes: RouteWithGates[]): RouteMergeCandidates {
  const lat0 = referenceLatitude(routes);
  const sections = routes.map((r) => routeSection(r, lat0));
  const { groups, dist } = connectedGroups(sections, ROUTE_MERGE_MAX_DISTANCE_M);

  const best = groups.reduce<number[]>((acc, g) => (g.length > acc.length ? g : acc), []);
  const candidateSet = new Set(best.length >= 2 ? best : []);
  const candidates = [...candidateSet].map((i) => routes[i]);
  const excluded: RouteMergeExclusion[] = [];
  for (let i = 0; i < routes.length; i++) {
    if (candidateSet.has(i)) continue;
    let nearest = Infinity;
    for (const j of candidateSet) nearest = Math.min(nearest, dist[i][j]);
    excluded.push({
      route: routes[i],
      distanceM: sections[i] && Number.isFinite(nearest) ? Math.round(nearest) : null,
    });
  }
  return { candidates, excluded };
}

/** The confirm text: how many routes merge, and which selected routes are left out and why. */
export function describeRouteMerge({ candidates, excluded }: RouteMergeCandidates): string {
  let msg = `Merge ${candidates.length} routes into one averaged route? ` +
    'Gates will be re-assigned where they intersect.';
  if (excluded.length) {
    const names = excluded.map(({ route, distanceM }) => {
      const label = route.description || route.address || route.id;
      const why = distanceM == null ? 'no gate position' : `${distanceM} m away`;
      return `  - ${label} (${why})`;
    });
    msg += `\n\n${excluded.length} selected route(s) cannot be merged ` +
      `(too far from the others, limit ${ROUTE_MERGE_MAX_DISTANCE_M} m) and will be left out:\n` +
      names.join('\n');
  }
  return msg;
}

/** The name the server gives a merged route when it is not given one. */
const DATED_MERGE_NAME = /^ROUTE-MERGE: \d{4}-\d{2}-\d{2}$/;

/**
 * A starting name for the merged route: the name of the candidate used the
 * most, so merging "Oxford Road" with two unnamed passes keeps "Oxford Road".
 * The server's dated default from an earlier merge does not count as a name.
 * Blank when nothing qualifies; the server then dates the route again.
 */
export function suggestMergedRouteName(candidates: RouteWithGates[]): string {
  const named = candidates.filter((r) => r.description?.trim() && !DATED_MERGE_NAME.test(r.description.trim()));
  if (!named.length) return '';
  return named.reduce((best, r) => (r.total_session_count > best.total_session_count ? r : best)).description.trim();
}

/**
 * What a route merge did, for the notice afterwards. `ok` is false when no
 * gate could be placed on the merged route: the server keeps the sources
 * then, so the merge did not really happen.
 */
export function describeRouteMergeResult(
  result: MergeRoutesResult, routeCount: number, name: string,
): { ok: boolean; text: string } {
  if (result.gates_reassigned === 0 && result.gates_skipped > 0) {
    return {
      ok: false,
      text: `Merged route${name ? ` "${name}"` : ''} created, but none of its ` +
        `${plural(result.gates_skipped, 'gate')} could be placed on it, so the source routes were kept.`,
    };
  }
  const into = name ? ` into "${name}"` : '';
  const parts = [`${plural(result.gates_reassigned, 'gate')} moved onto it`];
  if (result.gates_skipped > 0) {
    parts.push(`${plural(result.gates_skipped, 'gate')} could not be placed and stayed ` +
      `on ${result.gates_skipped === 1 ? 'its' : 'their'} route`);
  }
  if (result.identical_merged > 0) parts.push(`${plural(result.identical_merged, 'identical gate')} merged`);
  return { ok: true, text: `Merged ${plural(routeCount, 'route')}${into}. ${parts.join(', ')}.` };
}
